// usePrecheckStream - fahrt die 5 Precheck-Phasen visuell durch und ruft
// pro Werk /api/transport/package-precheck (Concurrency 3, Timeout 120s).
// Am Ende /api/transport/package-references fuer die globalen Referenzen.
// Nutzt intern useCompareStream fuer Phase-Timing. Der Hook stammt aus der
// entfernten V1-Transport-App und liegt seitdem hier, weil nur noch V2 ihn braucht.

import { useCallback, useState } from "react";
import { useCompareStream } from "./useCompareStream";
import { api } from "../../../utils/helpers";
import { useLang } from "../i18n";

const CONCURRENCY = 3;

async function runInBatches(tasks, worker, size = CONCURRENCY) {
  const out = {};
  for (let i = 0; i < tasks.length; i += size) {
    const batch = tasks.slice(i, i + size);
    const res = await Promise.all(batch.map(async (t) => {
      try {
        const r = await worker(t);
        return { key: t.key, value: r };
      } catch (e) {
        return { key: t.key, value: { error: e?.message || "precheck fehlgeschlagen" } };
      }
    }));
    for (const r of res) out[r.key] = r.value;
  }
  return out;
}

export function usePrecheckStream() {
  const { t } = useLang();
  const { phases, running, startCompare, reset } = useCompareStream();
  const [precheck, setPrecheck] = useState({});
  const [refs, setRefs] = useState([]);
  const [refsWarning, setRefsWarning] = useState(null);
  const [error, setError] = useState(null);

  const run = useCallback(async ({ sourceInstanceId, sourcePackageId, targetInstances, werke }) => {
    setPrecheck({});
    setRefs([]);
    setRefsWarning(null);
    setError(null);
    if (!sourceInstanceId || !sourcePackageId) return { precheck: {}, references: [] };
    try {
      await startCompare(api, {
        fetchAuth: async () => api(`/api/${sourceInstanceId}/transport/packages`),
        fetchSource: async () => api(`/api/${sourceInstanceId}/transport/package-artifacts/${encodeURIComponent(sourcePackageId)}`).catch(() => null),
        fetchTarget: async () => {
          const bucketList = werke && werke.length ? werke : [{ key: "std", kennung: "" }];
          const tasks = [];
          for (const inst of targetInstances || []) {
            for (const w of bucketList) {
              const key = `${inst.id}__${w.kennung || "std"}`;
              const body = {
                sourceInstanceId,
                targetInstanceId: inst.id,
                sourcePackageId,
                targetPackageId: sourcePackageId + (w.kennung || ""),
              };
              if (w.kennung) body.werkKennung = w.kennung;
              tasks.push({ key, body, werk: w.kennung ? w : null, instance: inst });
            }
          }
          const results = await runInBatches(tasks, async (t) => {
            const r = await api("/api/transport/package-precheck", {
              method: "POST",
              timeout: 120000,
              body: JSON.stringify(t.body),
            });
            return { ...r, werk: t.werk, instance: t.instance };
          });
          // Fehlerhafte Zielabfragen dürfen die letzte Liste nicht durch leere Zeilen ersetzen.
          const failures = Object.values(results).filter((r) => r.error || !Array.isArray(r.comparison));
          if (failures.length) throw new Error(failures.map((r) => r.error || t("err.precheckResponse")).join("; "));
          setPrecheck(results);
          return results;
        },
        fetchRefs: async (_apiFn, _params, collected) => {
          const targets = collected?.target || {};
          const firstWithComparison = Object.values(targets).find((c) => c && Array.isArray(c.comparison));
          if (!firstWithComparison) { setRefs([]); return { references: [] }; }
          const srcIds = [...new Set(firstWithComparison.comparison.filter((a) => !a.draft).map((a) => a.originalId || a.Id))];
          if (srcIds.length === 0) { setRefs([]); return { references: [] }; }
          const firstTargetId = firstWithComparison.instance?.id || targetInstances?.[0]?.id;
          try {
            const r = await api("/api/transport/package-references", {
              method: "POST",
              timeout: 120000,
              body: JSON.stringify({ sourceInstanceId, targetInstanceId: firstTargetId, sourcePackageId, artifactIds: srcIds }),
            });
            const list = Array.isArray(r?.references) ? r.references : [];
            setRefs(list);
            if (Array.isArray(r?.failedArtifacts) && r.failedArtifacts.length > 0) {
              setRefsWarning(`Globale Referenzen unvollständig: ${r.failedArtifacts.length} von ${srcIds.length} Artefakten konnten nicht gescannt werden.`);
            }
            return r;
          } catch (e) {
            setRefs([]);
            setRefsWarning(e?.message || "Globale Referenzen konnten nicht geladen werden");
            return { references: [] };
          }
        },
        computeDiff: async () => true,
      });
      return { precheck, references: refs };
    } catch (e) {
      setError(e?.message || "Vergleich fehlgeschlagen");
      throw e;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startCompare, t]);

  return { phases, running, run, reset, precheck, refs, refsWarning, error };
}
